import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { Indent } from './IndentExtension';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
}: RichTextEditorProps): JSX.Element | null {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Indent],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {/* Toolbar */}
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexWrap: 'wrap',
        }}
      >
        {/* Bold / Italic / Underline */}
        <Tooltip title="Negrita">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBold().run()}
              color={editor.isActive('bold') ? 'primary' : 'default'}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Itálica">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              color={editor.isActive('italic') ? 'primary' : 'default'}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Subrayado">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              color={editor.isActive('underline') ? 'primary' : 'default'}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Listas */}
        <Tooltip title="Lista con viñetas">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              color={editor.isActive('bulletList') ? 'primary' : 'default'}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              color={editor.isActive('orderedList') ? 'primary' : 'default'}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Indentación */}
        <Tooltip title="Aumentar sangría">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().indentParagraph().run()}
            >
              <FormatIndentIncreaseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reducir sangría">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor.chain().focus().outdentParagraph().run()}
            >
              <FormatIndentDecreaseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Undo / Redo */}
        <Tooltip title="Deshacer">
          <span>
            <IconButton
              size="small"
              disabled={disabled || !editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rehacer">
          <span>
            <IconButton
              size="small"
              disabled={disabled || !editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}
            >
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Área de edición */}
      <Box
        sx={{
          p: 2,
          minHeight: 300,
          cursor: disabled ? 'default' : 'text',
          '& .ProseMirror': {
            outline: 'none',
            minHeight: 280,
          },
          '& .ProseMirror p': { my: 0.5 },
          '& .ProseMirror ul, & .ProseMirror ol': { pl: 3 },
          '& .ProseMirror li': { my: 0.25 },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: `"${placeholder ?? ''}"`,
            color: 'text.disabled',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
        onClick={() => {
          if (!disabled) editor.commands.focus();
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
